import { PageableDataSource, GetDataParameters, FiltersModel, SortsModel } from "./table-paging";
import { v4 } from "uuid";

// This class is a wrapper around an existing data source proxy. The basic pattern is:
// Ag-grid -> proxy -> SDK consumer team's datasource -> proxy -> Ag-grid
// This proxy exists to translate Ag-grid concerns into ag-grid agnostic usage on behalf of our consumers.
export class PageableDataSourceProxy {
  public isBusyWithServer: boolean;
  private outstandingRequests = 0;
  private currentInfiniteModelSession: string;

  constructor(public wrappedSource: PageableDataSource) {
    this.currentInfiniteModelSession = v4();
  }

  public get isBusy() {
    return this.outstandingRequests !== 0;
  }

  public getRows(agGridGetDataArgs: any): PromiseLike<any> {
    let adaptedParams = this.adaptParameters(agGridGetDataArgs);
    let successCallback = <(dataSlice: any[], endIndex?: number) => void>agGridGetDataArgs.successCallback;
    let failCallback = <() => void>agGridGetDataArgs.failCallback;
    this.outstandingRequests++;
    // session affinity is used to track if the same logical session is still active after a series of async callbacks.
    // after async execution, the value of this.currentInfiniteModelSession could change to a different session (e.g.
    //  the table is reset for some reason). In this case, the callback will verify that the sessionAffinity has changed
    //  and short circuit response processing.
    let sessionAffinity = this.currentInfiniteModelSession;

    return this.wrappedSource
      .getData(adaptedParams)
      .then((success) => {
        try {
          let endRow = undefined;
          if (success.pageOfData.length !== agGridGetDataArgs.endRow - agGridGetDataArgs.startRow) {
            endRow = agGridGetDataArgs.startRow + success.pageOfData.length;
          }

          if (sessionAffinity == this.currentInfiniteModelSession) {
            successCallback(success.pageOfData, endRow);
          }
        } catch (e) {
          return Promise.reject(e);
        }
      })
      .then(undefined, (failure) => {
        if (sessionAffinity == this.currentInfiniteModelSession) {
          // Right now, we're not doing anything special when this happens. The assumption is that the caller is watching their
          //  data source execute and handling errors in a predictable way.
          failCallback();
        }
      })
      .then(
        () => {
          this.outstandingRequests--;
        },
        (error) => {
          this.outstandingRequests--;
          return Promise.reject(error);
        }
      );
  }

  public onInfiniteModelReset() {
    this.currentInfiniteModelSession = v4();
  }

  private adaptParameters(agGridGetDataArgs: any): GetDataParameters {
    return {
      startIndex: agGridGetDataArgs.startRow,
      endIndex: agGridGetDataArgs.endRow,
      sortModel: this.adaptSortModel(agGridGetDataArgs.sortModel),
      filterModel: this.adaptFilterModel(agGridGetDataArgs.filterModel)
    };
  }

  private adaptFilterModel(agGridFilterModel: any): FiltersModel {
    if (agGridFilterModel) {
      let filtersModel = <FiltersModel>{};

      Object.keys(agGridFilterModel).forEach((key) => {
        filtersModel[key] = {
          validValues: agGridFilterModel[key].filter.split(",")
        };
      });

      return filtersModel;
    }

    return undefined;
  }

  private adaptSortModel(agGridSortModel: any): SortsModel {
    if (agGridSortModel) {
      return {
        sorts: agGridSortModel.map((sortModel: any, index: number) => {
          let columnName = sortModel.colId;
          let isAscending = sortModel.sort == "asc";
          return {
            columnName,
            isAscending
          };
        })
      };
    }

    return undefined;
  }
}
